import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GraphResolver } from '../services/graphResolver';
import { useIdentity } from '../hooks/useIdentity';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useLoginStateMachine } from '../hooks/useLoginStateMachine';
import { ErrorDisplay } from './ErrorDisplay';
import { getPairsForMember } from '../utils/pairing';
import type { Membre, Action } from '../types/contract';
import type { PairConfig } from '../types/identity';

interface MemberWithDetails {
  membre: Membre;
  actionsLogin: Action[];
  pairs: PairConfig[];
  hasValidLoginPath: boolean;
}

export function MemberSelectionScreen(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { identity } = useIdentity();
  const { error, handleError, clearError } = useErrorHandler();
  const { state: loginState, dispatch } = useLoginStateMachine();
  const [members, setMembers] = useState<MemberWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const serviceUuid = searchParams.get('service') ?? '';

  useEffect(() => {
    if (serviceUuid === '') {
      handleError('Service UUID requis', 'MISSING_SERVICE');
      return;
    }
    void loadMembers();
  }, [serviceUuid, handleError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMembers = async (): Promise<void> => {
    if (identity === null || serviceUuid === '') {
      return;
    }

    setIsLoading(true);
    clearError();
    try {
      const graphResolver = new GraphResolver();
      const allMembres = graphResolver.getMembres();
      const allActions = graphResolver.getActions();

      // Filter membres that belong to this service
      const serviceMembres = allMembres.filter((m) =>
        m.datajson.services_uuid.includes(serviceUuid),
      );

      // Find login actions
      const loginActions = allActions.filter((action) => {
        const typesNames = action.types.types_names_chiffres ?? '';
        return typesNames.includes('login');
      });

      const membersWithDetails: MemberWithDetails[] = [];

      for (const membre of serviceMembres) {
        // Find login actions for this member
        const memberLoginActions = loginActions.filter((action) =>
          membre.actions_parents_uuid.includes(action.uuid),
        );

        // Get pairs for this member
        const pairs = getPairsForMember(membre.uuid);

        // Check if login path is valid
        const path = graphResolver.resolveLoginPath(serviceUuid, membre.uuid);
        const hasValidLoginPath = path?.statut === 'complet';

        membersWithDetails.push({
          membre,
          actionsLogin: memberLoginActions,
          pairs,
          hasValidLoginPath,
        });
      }

      if (membersWithDetails.length === 0) {
        handleError(
          'Aucun membre avec action login trouvé pour ce service',
          'NO_MEMBERS_WITH_LOGIN',
        );
      }

      setMembers(membersWithDetails);
    } catch (err) {
      handleError(err, 'Erreur lors du chargement des membres');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectMember = (membreUuid: string): void => {
    if (loginState === 'S_LOGIN_SELECT_MEMBER') {
      dispatch({ type: 'E_SELECT_MEMBER', membreUuid });
    }
    navigate(`/login?service=${serviceUuid}&membre=${membreUuid}`);
  };

  const handleBack = (): void => {
    if (loginState === 'S_LOGIN_SELECT_MEMBER') {
      dispatch({ type: 'E_BACK' });
    }
    navigate('/services');
  };

  if (serviceUuid === '') {
    return (
      <main>
        <h1>Sélection du membre</h1>
        <p>Service UUID requis</p>
        <button type="button" onClick={handleBack}>
          Retour
        </button>
      </main>
    );
  }

  return (
    <main>
      <h1>Sélection du membre</h1>
      <p role="status" aria-live="polite" className="sr-only">
        Service: {serviceUuid}
      </p>
      {error !== null && <ErrorDisplay error={error} onDismiss={clearError} />}
      <section aria-labelledby="members-list">
        <h2 id="members-list">Membres disponibles</h2>
        {isLoading ? (
          <p>Chargement...</p>
        ) : members.length === 0 ? (
          <p>
            Aucun membre avec action login trouvé pour ce service. Synchronisez
            d&apos;abord les données.
          </p>
        ) : (
          <ul role="list">
            {members.map((memberDetail) => {
              const { membre, actionsLogin, pairs, hasValidLoginPath } =
                memberDetail;
              return (
                <li key={membre.uuid}>
                  <div>
                    <h3>
                      {membre.datajson.label ?? `Membre ${membre.uuid.slice(0, 8)}`}
                    </h3>
                    <p>
                      <strong>UUID:</strong> {membre.uuid}
                    </p>
                    {membre.datajson.description_courte !== undefined && (
                      <p>{membre.datajson.description_courte}</p>
                    )}
                    <p>
                      <strong>Actions login:</strong> {actionsLogin.length}
                    </p>
                    {actionsLogin.length > 0 && (
                      <ul>
                        {actionsLogin.map((action) => (
                          <li key={action.uuid}>
                            Action: {action.uuid.slice(0, 8)}...
                          </li>
                        ))}
                      </ul>
                    )}
                    <p>
                      <strong>Pairs associés:</strong> {pairs.length}
                    </p>
                    {pairs.length > 0 && (
                      <ul>
                        {pairs.map((pair) => (
                          <li key={pair.uuid}>
                            {pair.is_local ? 'Local' : 'Distant'} -{' '}
                            {pair.can_sign ? 'Peut signer' : 'Ne peut pas signer'}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p>
                      <strong>Chemin login:</strong>{' '}
                      {hasValidLoginPath ? 'Complet et valide' : 'Incomplet'}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        handleSelectMember(membre.uuid);
                      }}
                      disabled={!hasValidLoginPath || actionsLogin.length === 0}
                    >
                      Sélectionner ce membre
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <div>
        <button type="button" onClick={handleBack}>
          Retour
        </button>
        <button
          type="button"
          onClick={loadMembers}
          disabled={isLoading}
        >
          {isLoading ? 'Chargement...' : 'Rafraîchir'}
        </button>
      </div>
    </main>
  );
}
